import { useMemo, useState } from 'react';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import DashboardLayout from '../components/DashboardLayout';
import { api, ApiError } from '@/lib/api';
import { useApiData } from '@/lib/useApi';
import type { AppointmentRow, UserRow, StudentRow } from '@/lib/types';
import { initials, fullName } from '@/lib/format';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

interface CalEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  student: string;
  counselor: string;
  type: string;
  status: string;
}

const statusColors: Record<string, string> = {
  confirmed: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-red-100 text-red-600',
  completed: 'bg-blue-100 text-blue-700',
};

function toEvent(a: AppointmentRow): CalEvent {
  return {
    id: a.id,
    title: a.studentName ? `${a.studentName} · ${a.title}` : a.title,
    start: new Date(a.startsAt),
    end: new Date(a.endsAt),
    student: a.studentName || a.title,
    counselor: a.counselorName || 'Unassigned',
    type: a.type || '—',
    status: a.status,
  };
}

function EventPopup({ event, onClose }: { event: CalEvent; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E1E5EA] bg-[#0B6E77]">
          <h3 className="text-sm font-semibold text-white">Appointment Details</h3>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/10 cursor-pointer"><i className="ri-close-line text-white text-base" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0B6E77] text-white font-bold text-sm">{initials(event.student)}</div>
            <div><p className="text-sm font-semibold text-[#3C3C3C]">{event.student}</p><p className="text-xs text-gray-500">{event.type}</p></div>
          </div>
          {[
            { label: 'Counselor', value: event.counselor, icon: 'ri-user-line' },
            { label: 'Date', value: format(event.start, 'MMM d, yyyy'), icon: 'ri-calendar-line' },
            { label: 'Time', value: `${format(event.start, 'h:mm a')} – ${format(event.end, 'h:mm a')}`, icon: 'ri-time-line' },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-3 bg-gray-50 rounded-lg p-2.5">
              <div className="w-7 h-7 flex items-center justify-center rounded-md bg-white border border-[#E1E5EA]"><i className={`${item.icon} text-xs text-gray-500`} /></div>
              <div><p className="text-[10px] text-gray-400">{item.label}</p><p className="text-xs font-medium text-gray-800">{item.value}</p></div>
            </div>
          ))}
          <div><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusColors[event.status.toLowerCase()] || 'bg-gray-100 text-gray-600'}`}>{event.status}</span></div>
        </div>
      </div>
    </div>
  );
}

const emptyForm = { title: '', type: 'Counseling', counselorId: '', studentId: '', startsAt: '', endsAt: '' };

function BookModal({ counselors, students, onClose, onBooked }: { counselors: UserRow[]; students: StudentRow[]; onClose: () => void; onBooked: () => void }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.counselorId) { setError('Please select a counselor'); return; }
    setSaving(true); setError('');
    try {
      await api.createAppointment({
        title: form.title,
        type: form.type,
        counselorId: Number(form.counselorId),
        studentId: form.studentId ? Number(form.studentId) : null,
        startsAt: form.startsAt.replace('T', ' '),
        endsAt: form.endsAt.replace('T', ' '),
        status: 'confirmed',
      });
      onBooked(); onClose();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not book appointment');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="px-6 py-5 border-b border-[#E1E5EA] flex justify-between">
          <h3 className="font-bold text-[#3C3C3C]">Book Appointment</h3>
          <button type="button" onClick={onClose} className="text-gray-400 cursor-pointer"><i className="ri-close-line text-xl" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div><label className="block text-sm font-medium text-[#3C3C3C] mb-1.5">Title</label><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Counseling session" className="w-full px-3.5 py-2.5 border border-[#E1E5EA] rounded-lg text-sm outline-none focus:border-[#0B6E77]" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-[#3C3C3C] mb-1.5">Counselor</label><select required value={form.counselorId} onChange={(e) => setForm({ ...form, counselorId: e.target.value })} className="w-full px-3.5 py-2.5 border border-[#E1E5EA] rounded-lg text-sm bg-white"><option value="">Select…</option>{counselors.map((c) => <option key={c.id} value={c.id}>{c.fullName}</option>)}</select></div>
            <div><label className="block text-sm font-medium text-[#3C3C3C] mb-1.5">Student (optional)</label><select value={form.studentId} onChange={(e) => setForm({ ...form, studentId: e.target.value })} className="w-full px-3.5 py-2.5 border border-[#E1E5EA] rounded-lg text-sm bg-white"><option value="">None</option>{students.map((s) => <option key={s.id} value={s.id}>{fullName(s.firstName, s.lastName)}</option>)}</select></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium text-[#3C3C3C] mb-1.5">Starts</label><input required type="datetime-local" value={form.startsAt} onChange={(e) => setForm({ ...form, startsAt: e.target.value })} className="w-full px-3.5 py-2.5 border border-[#E1E5EA] rounded-lg text-sm" /></div>
            <div><label className="block text-sm font-medium text-[#3C3C3C] mb-1.5">Ends</label><input required type="datetime-local" value={form.endsAt} onChange={(e) => setForm({ ...form, endsAt: e.target.value })} className="w-full px-3.5 py-2.5 border border-[#E1E5EA] rounded-lg text-sm" /></div>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-500 cursor-pointer">Cancel</button>
          <button type="submit" disabled={saving} className="px-5 py-2 bg-[#0B6E77] text-white rounded-lg text-sm font-semibold disabled:opacity-50 cursor-pointer">{saving ? 'Booking…' : 'Book'}</button>
        </div>
      </form>
    </div>
  );
}

export default function AppointmentsPage() {
  const { data, loading, reload } = useApiData<AppointmentRow[]>(() => api.appointments());
  const { data: users } = useApiData<UserRow[]>(() => api.users());
  const { data: students } = useApiData<StudentRow[]>(() => api.students());
  const [view, setView] = useState<string>(Views.MONTH);
  const [selectedEvent, setSelectedEvent] = useState<CalEvent | null>(null);
  const [date, setDate] = useState(new Date());
  const [showBook, setShowBook] = useState(false);

  const events = useMemo(() => (data ?? []).map(toEvent), [data]);

  const eventStyleGetter = () => ({
    style: { backgroundColor: '#0B6E77', borderRadius: '4px', color: '#0B6E77', border: 'none', fontSize: '11px', padding: '2px 4px' },
  });

  return (
    <DashboardLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 bg-white border border-[#E1E5EA] rounded-lg p-1">
            {[Views.MONTH, Views.WEEK, Views.DAY].map((v) => (
              <button key={v} onClick={() => setView(v)} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all cursor-pointer whitespace-nowrap capitalize ${view === v ? 'bg-[#0B6E77] text-white' : 'text-gray-500 hover:text-gray-700'}`}>{v}</button>
            ))}
          </div>
          <button onClick={() => setShowBook(true)} className="flex items-center gap-2 px-4 py-2 bg-[#0B6E77] text-white text-sm font-semibold rounded-lg hover:bg-[#085258] transition-colors cursor-pointer whitespace-nowrap">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-base" /></div>
            Book Appointment
          </button>
        </div>

        <div className="bg-white rounded-xl border border-[#E1E5EA] p-4 overflow-hidden">
          {loading && <div className="mb-2 text-xs text-gray-400">Loading appointments…</div>}
          <style>{`
            .rbc-calendar { font-family: 'Inter', sans-serif; }
            .rbc-header { font-size: 12px; font-weight: 600; color: #6B7280; padding: 8px 0; border-bottom: 1px solid #F3F4F6; }
            .rbc-today { background-color: #FBF7EB !important; }
            .rbc-off-range-bg { background-color: #FAFAFA; }
            .rbc-toolbar button { font-size: 13px; color: #374151; border-color: #E5E7EB; border-radius: 6px; }
            .rbc-toolbar button.rbc-active { background-color: #0B6E77; color: white; border-color: #0B6E77; }
            .rbc-toolbar button:hover { background-color: #F3F4F6; }
            .rbc-event { border-radius: 4px !important; }
            .rbc-date-cell { font-size: 12px; color: #374151; }
          `}</style>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 560 }}
            view={view as never}
            onView={(v) => setView(v)}
            date={date}
            onNavigate={(d) => setDate(d)}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={(event) => setSelectedEvent(event as CalEvent)}
            popup
          />
        </div>
      </div>

      {selectedEvent && <EventPopup event={selectedEvent} onClose={() => setSelectedEvent(null)} />}
      {showBook && <BookModal counselors={users ?? []} students={students ?? []} onClose={() => setShowBook(false)} onBooked={reload} />}
    </DashboardLayout>
  );
}
